import { dateUtils } from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import { Alert, Tooltip } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { paperStyles, zIndexStyles } from '@festina-life/ui/libs'
import {
  Icon,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@festina-life/ui/structure'
import cx from 'classnames'

import type { UserActionData } from '../../uiModels/types'
import { UserAction } from './UserAction'

type Props = {
  actions: UserActionData[]
  variant?: 'compact' | 'normal'
  canApproveOperation?: boolean
  canUnApproveOperation?: boolean
  onApprove?: () => Promise<void>
  onUnApprove?: () => Promise<void>
  isMutating?: boolean
}

export const OperationApprovalStatus = ({
  variant = 'normal',
  actions,
  canApproveOperation,
  canUnApproveOperation,
  onApprove,
  onUnApprove,
  isMutating,
}: Props) => {
  const translate = useTranslate()
  const creation = actions.find((action) => action.action === 'create')
  const approval = actions.find((action) => action.action === 'approve')
  const isApproved = !!approval?.actionTime
  const isApprovalDisabled = !canApproveOperation || !onApprove
  const isUnApprovalDisabled = !canUnApproveOperation || !onUnApprove

  return variant === 'compact' ? (
    <Tooltip
      tip={
        <div className={cx('grid gap-2 grid-cols-1')}>
          <UserAction
            action="create"
            userId={creation?.userId}
            actionTime={creation?.actionTime}
          />
          {isApproved && (
            <UserAction
              action="approve"
              userId={approval.userId}
              actionTime={dateUtils.dateWithTimeAsStr(approval.actionTime)}
            />
          )}
        </div>
      }
    >
      <div
        className={cx(
          'flex items-center gap-x-1',
          isApproved
            ? 'text-success-500!'
            : 'text-slate-300 dark:text-slate-600',
        )}
      >
        <Icon
          name={isApproved ? 'CheckCircle' : 'QuestionMarkCircle'}
          color="unset"
        />
      </div>
    </Tooltip>
  ) : (
    <div className="flex items-center gap-x-2">
      <Popover className="relative">
        <PopoverButton className="flex items-center gap-x-2">
          {isApproved ? (
            <UserAction
              action="approve"
              variant="compact"
              userId={approval?.userId}
              actionTime={approval?.actionTime}
            />
          ) : (
            <UserAction
              action="create"
              variant="compact"
              userId={creation?.userId}
              actionTime={creation?.actionTime}
            />
          )}
          <Icon name="ChevronDown" />
        </PopoverButton>
        <PopoverPanel
          isShowingBackdrop
          className={cx(
            'fixed rounded-lg p-2 shadow-lg max-w-sm w-sm flex flex-col gap-2',
            paperStyles({ isFaded: true, borders: ['all'] }),
            zIndexStyles('popovers'),
          )}
          anchor="bottom start"
        >
          <UserAction
            action="create"
            userId={creation?.userId}
            actionTime={creation?.actionTime}
          />
          <div className="flex flex-col gap-2">
            {isApproved ? (
              <>
                <UserAction
                  action="approve"
                  userId={approval?.userId}
                  actionTime={approval?.actionTime}
                />
                <Button
                  variant="secondary"
                  disabled={isUnApprovalDisabled}
                  startIcon="ArrowUturnLeft"
                  onClick={canUnApproveOperation ? onUnApprove : undefined}
                  isLoading={isMutating}
                >
                  {translate('operationStatus.unApprove')}
                </Button>
                {isUnApprovalDisabled && (
                  <Alert
                    severity="error"
                    title={translate('operationStatus.unApprove.tooltip')}
                  />
                )}
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={isApprovalDisabled}
                  startIcon="CheckCircle"
                  onClick={canApproveOperation ? onApprove : undefined}
                  isLoading={isMutating}
                >
                  {translate('operationStatus.approve')}
                </Button>
                {isApprovalDisabled && (
                  <Alert
                    severity="error"
                    title={translate('operationStatus.approve.tooltip')}
                  />
                )}
              </>
            )}
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  )
}
